import { useEffect, useRef, useState, useSyncExternalStore } from "react";

/**
 * Confirmations and toasts, callable from anywhere.
 *
 * `window.confirm` blocked the whole tab, looked like it came from the
 * browser rather than the app, and could only say OK. `confirmAction` is a
 * drop-in for it — `if (await confirmAction({...}))` — that names the action
 * on its button and marks destructive ones red. `toast` is the one signal
 * that something saved, wherever on the page it happened.
 *
 * Both are plain functions over a tiny store so mutation callbacks can use
 * them without threading context through; `<FeedbackHost />` in the app
 * shell renders whatever is pending.
 */

export interface ConfirmOptions {
  title: string;
  message?: string | undefined;
  /** The button: say what happens, e.g. "Delete campaign". */
  confirmLabel: string;
  /** Red button, and Cancel takes focus so Enter does not destroy anything. */
  destructive?: boolean | undefined;
}

type Tone = "ok" | "bad" | "info";
interface Toast {
  id: number;
  message: string;
  tone: Tone;
}

interface State {
  confirm: (ConfirmOptions & { resolve: (ok: boolean) => void }) | null;
  toasts: Toast[];
}

let state: State = { confirm: null, toasts: [] };
const listeners = new Set<() => void>();
const emit = (next: State) => {
  state = next;
  for (const l of listeners) l();
};
let nextId = 1;

export function confirmAction(options: ConfirmOptions): Promise<boolean> {
  // A second request while one is open answers the first "no".
  state.confirm?.resolve(false);
  return new Promise((resolve) => emit({ ...state, confirm: { ...options, resolve } }));
}

export function toast(message: string, tone: Tone = "ok"): void {
  const id = nextId++;
  emit({ ...state, toasts: [...state.toasts, { id, message, tone }].slice(-4) });
  setTimeout(() => emit({ ...state, toasts: state.toasts.filter((t) => t.id !== id) }), 4000);
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function FeedbackHost() {
  const current = useSyncExternalStore(subscribe, () => state);
  return (
    <>
      {current.confirm && <ConfirmDialog request={current.confirm} />}
      <div className="toasts" aria-live="polite">
        {current.toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`} role="status">
            {t.message}
          </div>
        ))}
      </div>
    </>
  );
}

function ConfirmDialog({
  request,
}: {
  request: ConfirmOptions & { resolve: (ok: boolean) => void };
}) {
  const cancel = useRef<HTMLButtonElement>(null);
  const ok = useRef<HTMLButtonElement>(null);
  const [done, setDone] = useState(false);

  const answer = (yes: boolean) => {
    if (done) return;
    setDone(true);
    request.resolve(yes);
    emit({ ...state, confirm: null });
  };

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    (request.destructive ? cancel : ok).current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        request.resolve(false);
        emit({ ...state, confirm: null });
      }
    };
    // Capture, so Esc closes this dialog and not a drawer underneath it.
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      opener?.focus?.();
    };
  }, [request]);

  return (
    <div className="dialog-root">
      <button
        type="button"
        className="drawer-backdrop"
        aria-label="Dismiss"
        tabIndex={-1}
        onClick={() => answer(false)}
      />
      <div className="dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title">
        <h2 id="confirm-title">{request.title}</h2>
        {request.message && <p>{request.message}</p>}
        <div className="dialog-actions">
          <button type="button" className="btn ghost" ref={cancel} onClick={() => answer(false)}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn${request.destructive ? " danger" : ""}`}
            ref={ok}
            onClick={() => answer(true)}
          >
            {request.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
